import React, { useEffect, useState, useCallback } from 'react';
import { isRawInternalId } from '../../../lib/customerIdentity';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { Activity, Cable, Inbox, ShieldAlert, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { tenantHeaders } from '../../../lib/tenantSession';

// Tenant Business-Plane IT view. Tenant-scoped data ONLY — platform/system
// infrastructure telemetry belongs to the SuperAdmin/Owner plane and is never
// shown here. All cards below render LIVE tenant data (connectors, data volumes,
// recent escalations), not placeholders.
const API = process.env.REACT_APP_BACKEND_URL;
const CONNECTOR_LABELS = { whatsapp: 'WhatsApp', email: 'Email', website: 'Website' };

const getToken = async () =>
  (auth.currentUser ? await auth.currentUser.getIdToken() : null) || localStorage.getItem('crm_access_token') || '';

const ago = (iso, t) => {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 60000;
  if (isNaN(d)) return '';
  if (d < 1) return t('pcb.it.justNow');
  if (d < 60) return t('pcb.it.minAgo', { n: Math.round(d) });
  if (d < 1440) return t('pcb.it.hourAgo', { n: Math.round(d / 60) });
  return t('pcb.it.dayAgo', { n: Math.round(d / 1440) });
};

const ITDashboard = () => {
  const navigate = useNavigate();
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();
  const brandId = currentBusiness?.brandId;

  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState({});
  const [volumes, setVolumes] = useState({ conversations: null, escalations: null });
  const [events, setEvents] = useState([]);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    if (!brandId) { setLoading(false); return; }
    setLoading(true);
    const token = await getToken();
    const h = { Authorization: `Bearer ${token}`, ...tenantHeaders() };
    const errs = {};

    const getJson = async (url) => { const r = await fetch(url, { headers: h }); if (!r.ok) throw new Error(String(r.status)); return r.json(); };

    // IT/Integration is intentionally denied ticket endpoints by RBAC — only call
    // IT-allowed tenant endpoints (integrations, conversations, escalations).
    const [intg, conv, esc] = await Promise.allSettled([
      getJson(`${API}/api/v1/tenants/${brandId}/integrations`),
      getJson(`${API}/api/v1/conversations/business/${brandId}`),
      getJson(`${API}/api/v1/escalations/business/${brandId}`),
    ]);

    if (intg.status === 'fulfilled') setIntegrations(intg.value.integrations || {}); else errs.integrations = true;
    const escList = esc.status === 'fulfilled' ? (esc.value.escalations || []) : [];
    setVolumes({
      conversations: conv.status === 'fulfilled' ? (conv.value.conversations || []).length : null,
      escalations: esc.status === 'fulfilled' ? escList.length : null,
    });
    if (conv.status !== 'fulfilled') errs.conversations = true;
    if (esc.status === 'fulfilled') setEvents(escList.slice(0, 6)); else errs.events = true;

    setErrors(errs);
    setLoading(false);
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  const configured = Object.entries(integrations).filter(([, c]) => c && typeof c === 'object');
  const active = configured.filter(([, c]) => c.enabled);

  if (!brandId) return (
    <div className="flex-1 p-6"><Card><CardContent className="py-10 text-center text-gray-600">
      {t('pg.it.workspaceNotLinked')}
    </CardContent></Card></div>
  );

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5" /> {t('pg.it.title')}
          </h1>
          <p className="text-gray-600">{t('pg.it.tenantIntegrationOps')} {currentBusiness?.businessName || brandId}</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}
        </Button>
      </div>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          {t('pg.it.tenantScopedNotice')}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Connectors — live tenant integrations */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Cable className="h-4 w-4" />{t('pg.it.connectors')}</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="py-6 text-center text-gray-400 text-sm">{t('pg.it.loading')}</div>
              : errors.integrations ? <div className="py-6 text-center text-red-600 text-sm">{t('pg.it.failedConnectors')}</div>
              : configured.length === 0 ? (
                <div className="flex flex-col items-center text-center py-6 text-gray-500">
                  <Inbox className="h-8 w-8 mb-2" />
                  <p className="text-sm">{t('pg.it.noConnectors')}</p>
                  <Button variant="outline" className="mt-3" onClick={() => navigate('/business/integration-hub')}>{t('pg.it.openIntegrationHub')}</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm text-gray-600">{t('pg.it.activeConfigured', { active: active.length, configured: configured.length })}</div>
                  <div className="space-y-2">
                    {configured.map(([key, cfg]) => (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span>{CONNECTOR_LABELS[key] || key}</span>
                        <Badge className={cfg.enabled ? 'bg-green-500' : ''} variant={cfg.enabled ? undefined : 'secondary'}>
                          {cfg.enabled ? <><CheckCircle2 className="h-3 w-3 mr-1" />{t('pg.it.enabled')}</> : <><XCircle className="h-3 w-3 mr-1" />{t('pg.it.disabled')}</>}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/business/integration-hub')}>{t('pg.it.openIntegrationHub')}</Button>
                </div>
              )}
          </CardContent>
        </Card>

        {/* Telemetry — live tenant data volumes */}
        <Card>
          <CardHeader><CardTitle className="text-base">{t('pg.it.dataVolume')}</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="py-6 text-center text-gray-400 text-sm">{t('pg.it.loading')}</div> : (
              <div className="space-y-4 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('pg.it.conversations')}</span>
                  <span className="text-xl font-bold text-gray-900">{volumes.conversations ?? '—'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('pg.it.escalations')}</span>
                  <span className="text-xl font-bold text-gray-900">{volumes.escalations ?? '—'}</span>
                </div>
                <p className="text-xs text-gray-400">{t('pg.it.liveTenantCounts')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Events — live recent escalations */}
        <Card>
          <CardHeader><CardTitle className="text-base">{t('pcb.it.recentEscalations')}</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="py-6 text-center text-gray-400 text-sm">{t('pg.it.loading')}</div>
              : events.length === 0 ? (
                <div className="flex flex-col items-center text-center py-6 text-gray-500">
                  <Inbox className="h-8 w-8 mb-2" /><p className="text-sm">{t('pcb.it.noRecentEscalations')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {events.map((e, i) => (
                    <div key={e.id || i} className="flex items-center justify-between text-sm border-b last:border-0 pb-1">
                      <span className="truncate max-w-[55%]">{e.customer_name && !isRawInternalId(e.customer_name) ? e.customer_name : t('pcb.it.escalation')}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs capitalize">{e.status || 'pending'}</Badge>
                        <span className="text-xs text-gray-400">{ago(e.created_at, t)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <Button onClick={() => navigate('/business/settings?tab=integrations')}>{t('pcb.it.manageIntegrations')}</Button>
      </div>
    </div>
  );
};

export default ITDashboard;
